import { PropsWithChildren, ReactNode, forwardRef } from 'react';

import classNames from 'classnames';

import { ScrollTopButton } from '../ScrollTopButton';
import { BackstagePage } from './BackstagePage';
import { Content } from './Content';
import { Header, HeaderProps } from './Header';
import { HeaderContent } from './HeaderContent';
import { NarrowBoxProps } from './NarrowBox';
import { PageTitle } from './PageTitle';
import { SidebarContent, SidebarContentProps } from './SidebarContent';

import styles from './PageLayout.module.css';

export type PageLayoutProps = PropsWithChildren &
  Pick<
    HeaderProps,
    | 'title'
    | 'headerBreadcrumbs'
    | 'headerTabs'
    | 'stickyHeader'
    | 'stickyHeaderTabs'
    | 'headerTabsRef'
    | 'isLoading'
    | 'pageTitleOverride'
    | 'enableMargin'
  > &
  Pick<SidebarContentProps, 'sideBarRight' | 'sideBarLeft'> & {
    narrow?: boolean | NarrowBoxProps['width'];
    headerContent?: ReactNode;
    scrollButton?: boolean;
    noPadding?: boolean;
    floatingHeader?: ReactNode;
    subheader?: ReactNode;
    fullViewport?: boolean;
    contentOverflow?: 'auto' | 'hidden';
  };

export const PageLayout = forwardRef<HTMLDivElement, PageLayoutProps>(
  (
    {
      children,
      floatingHeader,
      sideBarRight,
      sideBarLeft,
      title,
      enableMargin,
      headerBreadcrumbs,
      headerTabs,
      headerContent,
      stickyHeader,
      stickyHeaderTabs,
      headerTabsRef,
      isLoading,
      pageTitleOverride,
      scrollButton = false,
      narrow,
      noPadding,
      subheader,
      fullViewport,
      contentOverflow = 'hidden',
    },
    ref,
  ) => {
    const hasHeader =
      title ||
      headerBreadcrumbs ||
      headerTabs ||
      headerContent ||
      subheader ||
      isLoading;

    return (
      <BackstagePage ref={ref} fullViewport={fullViewport}>
        {floatingHeader}
        {scrollButton && <ScrollTopButton  data-track-source-anchor="3a3b21482c;page-layout-scroll-top-button"/>}
        {hasHeader ? (
          <Header
            enableMargin={enableMargin}
            title={title}
            subheader={subheader}
            isLoading={isLoading}
            pageTitleOverride={pageTitleOverride}
            narrow={narrow}
            headerBreadcrumbs={headerBreadcrumbs}
            headerTabs={headerTabs}
            stickyHeader={stickyHeader}
            stickyHeaderTabs={stickyHeaderTabs}
            headerTabsRef={headerTabsRef}
          >
            {headerContent ? (
              <HeaderContent>{headerContent}</HeaderContent>
            ) : null}
          </Header>
        ) : (
          pageTitleOverride && <PageTitle title={pageTitleOverride} />
        )}

        <Content
          narrow={narrow}
          className={classNames(noPadding ? undefined : styles.content, {
            [styles.fullViewportContent]: fullViewport,
            [styles.scrollableFullViewportContent]:
              fullViewport && contentOverflow === 'auto',
          })}
          noPadding={noPadding}
        >
          {sideBarLeft || sideBarRight ? (
            <SidebarContent
              sideBarLeft={sideBarLeft}
              sideBarRight={sideBarRight}
            >
              {children}
            </SidebarContent>
          ) : (
            children
          )}
        </Content>
      </BackstagePage>
    );
  },
);
PageLayout.displayName = 'PageLayout';
